import { BORDERS, COLORS, SHADOWS, SPACING } from '@alto/design-library-tokens';
import React, { useState } from 'react';
import { Pressable } from 'react-native';
import styled from 'styled-components/native';
import { getBrand, useScreenSize } from '../../../utils';
import { MdPadding, Row, XxsPadding } from '../../containers';
import { Caption, Text } from '../../typography';
import { getOptionHorizontalPadding } from './getOptionHorizontalPadding';

type TouchableProps = {
  disabled?: boolean;
  selected: boolean;
  isPressed: boolean;
  showSelected: boolean;
};

type Props = {
  readonly disabled?: boolean;
  readonly label: string;
  readonly sublabel?: string;
  readonly onPress: () => void;
  readonly selected: boolean;
  readonly itemsQuantity: number;
  readonly showSelected: boolean;
  readonly testID?: string;
};

const getTextColor = (showSelected: boolean, selected: boolean, disabled = false) => {
  if (disabled) {
    return COLORS.TEXT_COLORS.DISABLED;
  }

  if (showSelected && selected) {
    return COLORS.TEXT_COLORS.WHITE;
  }

  const brand = getBrand();
  if (brand === 'fuzehealth' || brand === 'fuzerx') {
    return COLORS.PALETTE.PRIMARY.DARKEST;
  }

  return COLORS.TEXT_COLORS.SECONDARY_DARK;
};

const getBackgroundColor = (isPressed: boolean, selected: boolean, disabled = false) => {
  if (selected && disabled) {
    return COLORS.PALETTE.GREYSCALE.DEFAULT;
  }
  return isPressed && !selected ? COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER : COLORS.BACKGROUND_COLORS.TRANSPARENT;
};

const getBorderColor = (isPressed: boolean, selected: boolean) => {
  return !selected && isPressed ? COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER : COLORS.BACKGROUND_COLORS.TRANSPARENT;
};

const OptionContainer = styled(XxsPadding)<{ itemsQuantity: number }>`
  width: ${({ itemsQuantity }) => (itemsQuantity ? `${100 / itemsQuantity}%` : 'auto')};
`;

const StyledPressable = styled(Pressable)<TouchableProps>`
  flex-grow: 1;
  background-color: ${({ disabled, selected, isPressed }) => getBackgroundColor(isPressed, selected, disabled)};
  border: 1px solid ${({ selected, isPressed }) => getBorderColor(isPressed, selected)};
  border-radius: ${BORDERS.RADIUS.XL.px};
  ${({ showSelected, selected, disabled }) =>
    !disabled &&
    showSelected &&
    selected &&
    `background-color: ${COLORS.BACKGROUND_COLORS.SECONDARY}; ${SHADOWS.BOTTOM};
  `}
`;

export const StyledMdPadding = styled(MdPadding)`
  width: 100%;
`;

export const ButtonSelectorOption = ({
  disabled = false,
  label,
  sublabel,
  onPress,
  selected,
  itemsQuantity,
  showSelected,
  testID,
}: Props) => {
  const [isTouchablePressed, setIsTouchablePressed] = useState(false);
  const { isXSScreen } = useScreenSize();
  const horizontalPadding = getOptionHorizontalPadding(isXSScreen, itemsQuantity);

  return (
    <OptionContainer
      itemsQuantity={itemsQuantity}
      topPadding={SPACING.STATIC.NONE}
      bottomPadding={SPACING.STATIC.NONE}
    >
      <StyledPressable
        accessibilityLabel={label}
        accessibilityRole="radio"
        accessibilityState={{
          disabled,
          selected,
          checked: selected,
        }}
        // @ts-expect-error issue with react-native-web types
        accessibilityChecked={selected}
        disabled={disabled}
        isPressed={isTouchablePressed}
        onPress={onPress}
        onPressIn={() => {
          setIsTouchablePressed(true);
        }}
        onPressOut={() => {
          setIsTouchablePressed(false);
        }}
        selected={selected}
        showSelected={showSelected}
        testID={testID}
      >
        <Row
          center
          flexGrow={1}
        >
          <StyledMdPadding
            topPadding={SPACING.STATIC.XS}
            bottomPadding={SPACING.STATIC.XS}
            leftPadding={horizontalPadding}
            rightPadding={horizontalPadding}
          >
            <Text
              center
              textSize="mini"
              fontFamily="semibold"
              color={getTextColor(showSelected, selected, disabled)}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {label}
            </Text>
            {sublabel ? (
              <Caption
                center
                fontFamily="semibold"
                color={getTextColor(showSelected, selected, disabled)}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {sublabel}
              </Caption>
            ) : null}
          </StyledMdPadding>
        </Row>
      </StyledPressable>
    </OptionContainer>
  );
};
